import {
    applyUkgTimeZoneTransformationToProps,
    removeUkgTimeZoneTransformationToProps,
    removeBrowserTimezoneConversionToProps,
    applyUkgUserTimeZoneTransformation,
    removeBrowserTimezoneConversion,
} from '@commonClient/services/ukgDateTime';
import { WeeklyRuleTypeNames } from '@enums/index';

const listOfDateParamsToConvertForUser = [
    'employeeSubmissionEndTime',
    'employeeSubmissionStartTime',
    'endBiddingTime',
    'previousSubmissionPeriodEndDate',
    'publishedDate',
    'startBiddingTime',
    'statusDate',
    'submissionPeriodEndDate',
    'submissionPeriodStartDate',
    'submittedDate',
];
const listOfDateParamsToConvertForBrowser = [
    'employeePeriodEndDate',
    'employeePeriodStartDate',
    'requestPeriodStartDate',
    'requestPeriodEndDate',
];
export const removeUserTimeZoneConversionMapping = removeUkgTimeZoneTransformationToProps(listOfDateParamsToConvertForUser);
export const removeBrowserTimeZoneConversionMapping = (bid) => {
    bid = removeBrowserTimezoneConversionToProps(listOfDateParamsToConvertForBrowser)(bid);
    const mappedAwardedResult = bid?.bidSelection?.awardedResult?.awardedSelections?.map((selection) => ({
        ...selection,
        selectedPtos: selection.selectedPtos?.map((pto) => ({
            ...pto,
            startDate: removeBrowserTimezoneConversion(pto.startDate),
            endDate: removeBrowserTimezoneConversion(pto.endDate),
        })),
    }));
    const mappedSelectedPtos = bid?.bidSelection?.selectedPtos?.map((selection) => ({
        ...selection,
        selectedPtos: selection.selectedPtos?.map((pto) => ({
            ...pto,
            startDate: removeBrowserTimezoneConversion(pto.startDate),
            endDate: removeBrowserTimezoneConversion(pto.endDate),
        })),
    }));
    return {
        ...bid,
        bidSelection: {
            ...bid.bidSelection,
            awardedResult: {
                awardedSelections: mappedAwardedResult,
            },
            selectedPtos: mappedSelectedPtos 
        } 
    };
};

export const applyManagementTimeZoneConversionMapping = (bid) => {
    const dateParamsToApplyConversion = [
        'submissionPeriodEndDate',
        'submissionPeriodStartDate',
        'requestPeriodStartDate',
        'requestPeriodEndDate',
        'cancelledDateTime',
        'publishedDateTime',
        'lastUpdate',
        'submittedDate',
    ];

    /** @type {VBD.IManagerBidProfile} */
    bid = applyUkgTimeZoneTransformationToProps(dateParamsToApplyConversion)(bid);
    const mappedEmployeesInfos = bid?.employeesInfos?.map((x) => ({
        ...x,
        submissionStart: x?.submissionStart ? applyUkgUserTimeZoneTransformation(x?.submissionStart) : undefined,
        submissionEnd: x?.submissionEnd ? applyUkgUserTimeZoneTransformation(x?.submissionEnd) : undefined,
    }));
    const mappedEmployeesBidSelections = bid?.employeesBidSelections?.map((x) => ({
        ...x,
        awardedResult: {
            ...x?.awardedResult,
            awardedSelections: x?.awardedResult?.awardedSelections?.map((selection) => ({
                ...selection,
                selectedPtos: selection.selectedPtos?.map((pto) => ({
                    ...pto,
                    startDate: removeBrowserTimezoneConversion(pto.startDate),
                    endDate: removeBrowserTimezoneConversion(pto.endDate),
                })),
            }))
        },
        selectedPtos: x?.selectedPtos?.map((selection) => ({
            ...selection,
            selectedPtos: selection.selectedPtos?.map((pto) => ({
                ...pto,
                startDate: removeBrowserTimezoneConversion(pto.startDate),
                endDate: removeBrowserTimezoneConversion(pto.endDate),
            })),
        })),
        submittedBidDate: x?.submittedBidDate ? applyUkgUserTimeZoneTransformation(x?.submittedBidDate) : undefined,
    }));

    return { ...bid, employeesInfos: mappedEmployeesInfos, employeesBidSelections: mappedEmployeesBidSelections };
};

export const applyUserTimeZoneConversionMapping = (bid) => {
    /** @type {VBD.IManagerBidProfile} */
    bid = applyUkgTimeZoneTransformationToProps(listOfDateParamsToConvertForUser)(bid);

    // Ensure date fields are never undefined to prevent Redux serialization issues
    const safeBid = {
        ...bid,
        submissionPeriodStartDate: bid.submissionPeriodStartDate || null,
        submissionPeriodEndDate: bid.submissionPeriodEndDate || null,
        requestPeriodStartDate: bid.requestPeriodStartDate || null,
        requestPeriodEndDate: bid.requestPeriodEndDate || null,
        employeePeriodStartDate: bid.employeePeriodStartDate || null,
        employeePeriodEndDate: bid.employeePeriodEndDate || null,
        startBiddingTime: bid.startBiddingTime || null,
        endBiddingTime: bid.endBiddingTime || null,
        rankedEmployees: bid?.rankedEmployees?.map((r) => ({
            ...r,
            submissionStart: r?.submissionStart ? applyUkgUserTimeZoneTransformation(r.submissionStart) : null,
            submissionEnd: r?.submissionEnd ? applyUkgUserTimeZoneTransformation(r.submissionEnd) : null,
        })),
    };

    return safeBid;
};

export const getWeeklyRuleTypeName = (weeklyRuleType) => WeeklyRuleTypeNames[weeklyRuleType];

export const getTransformResponse = (bid) => {
    bid = applyUserTimeZoneConversionMapping(bid);
    bid = removeBrowserTimeZoneConversionMapping(bid);

    // Ensure date fields are never undefined to prevent Redux serialization issues
    bid.submissionPeriodStartDate = bid.submissionPeriodStartDate || null;
    bid.submissionPeriodEndDate = bid.submissionPeriodEndDate || null;
    bid.requestPeriodStartDate = bid.requestPeriodStartDate || null;
    bid.requestPeriodEndDate = bid.requestPeriodEndDate || null;
    bid.employeePeriodStartDate = bid.employeePeriodStartDate || null;
    bid.employeePeriodEndDate = bid.employeePeriodEndDate || null;
    bid.startBiddingTime = bid.startBiddingTime || null;
    bid.endBiddingTime = bid.endBiddingTime || null;

    bid.submissionPeriods = bid?.submissionPeriods?.map((submissionPeriod) => ({
        startDate: new Date(submissionPeriod?.startDate),
        endDate: new Date(submissionPeriod?.endDate)
    }));
    bid.calendarRuleOverrides = bid.calendarRuleOverrides.map((override) => ({
        calendarRuleOverrideId: override.calendarRuleOverrideId,
        employeeGroupId: override.employeeGroupId,
        calendarRulePeriods: override.calendarRulePeriods.map((calendarRulePeriods) =>
            removeBrowserTimeZoneConversionMapping(calendarRulePeriods))
    }))
    return bid;
};